import React, { useEffect } from "react";
import "./App.css";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import routes from "./routes";

import { StyledEngineProvider, Theme } from "@mui/material/styles";
import { observer } from "mobx-react-lite";
import { useStores } from "./store";

import { SnackbarProvider, useSnackbar } from "notistack";
import { isEqual } from "./helpers";

import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/ru";
import { DepartmentEnum } from "./store/department.enum";
import { SettingsConsumer } from "context/SettingsContext";
import { gql } from '@apollo/client';
import { client } from './store';
import ThemeComponent from "./theme/ThemeComponent";
import 'photoswipe/photoswipe.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

dayjs.locale('ru');
dayjs.extend(LocalizedFormat);

declare module '@mui/styles/defaultTheme' {
	interface DefaultTheme extends Theme {
	}
}


const CheckAuth = observer(() => {
	const routing = useRoutes(routes);
	const {snake, setSnakeMessage, userStore} = useStores();
	const {enqueueSnackbar} = useSnackbar();
	const navigate = useNavigate();
	const location = useLocation();

  useEffect(() => {
    let pending = false;
    const check = async () => {
      if (pending || document.visibilityState === 'hidden' || !localStorage.getItem('user_token')) return;
      pending = true;
      try {
        await client.query({ query: gql`query SessionActive { auth_sessionActive }`, fetchPolicy: 'no-cache' });
      } catch { /* The auth error link clears only this application's stale session. */ }
      finally { pending = false; }
    };
    const timer = window.setInterval(() => { void check(); }, 15_000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    void check();
    return () => { window.clearInterval(timer); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, []);

	useEffect(() => {
		if (!isEqual(snake.msg, '')) {
			enqueueSnackbar(snake.msg.replace('Unexpected error value: ', '').replace(/\"/g, ''), {variant: snake.type});
			setSnakeMessage('')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [snake.msg, enqueueSnackbar, setSnakeMessage, snake.type]);

	// Эффект для перенаправления после успешного логина
	useEffect(() => {
		const isOnLoginPage = location.pathname === '/login' || location.pathname.endsWith('/login');
		const hasUser = userStore?.currentUser && userStore?.currentUser?.user && userStore?.currentUser?.token;
		
		if (hasUser && isOnLoginPage) {
			const startLocation = localStorage.getItem(`startLocation`);
			const redirectTo = startLocation && startLocation !== '/login' ? startLocation : '/';
			
			// Используем navigate, а если не сработает - window.location как fallback
			navigate(redirectTo, { replace: true });
			
			// Задержка для надежности, если navigate не сработал
			setTimeout(() => {
				if (window.location.pathname === '/login' || window.location.pathname.endsWith('/login')) {
					window.location.href = redirectTo;
				}
			}, 100);
			
			localStorage.removeItem('startLocation');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userStore?.currentUser, userStore?.isLoggedIn, location.pathname]);

	useEffect(() => {
		if (location) {
			const hasUser = userStore?.currentUser && userStore?.currentUser?.user && userStore?.currentUser?.token;
			if (!hasUser) { // Пользователь не авторизован!
				// Исключаем страницы login и sso из редиректа
				const isAuthPage = location.pathname.endsWith('login') || location.pathname.includes('/sso');
				if (!isAuthPage) {
					localStorage.setItem(`startLocation`, location.pathname); // Сохраняем путь с которого пришёл пользователь
					navigate('/login') // Перенаправляем на строницу логина!
					const loggedOut = localStorage.getItem('user_logged_out');
					if (loggedOut && JSON.parse(loggedOut)) { //Пользователь вышел самостоятельно, перезагружаем страницу для сброса данных стора
						window.location.reload();
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, userStore?.currentUser, userStore?.isLoggedIn]);

	return routing
});

const App = observer(() => {

	return (
			<StyledEngineProvider injectFirst>
				<SettingsConsumer>
					{({ settings }) => {
						return <ThemeComponent settings={settings}>
							<SnackbarProvider
								maxSnack={3}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
							>
								<CheckAuth/>
							</SnackbarProvider>
						</ThemeComponent>
					}}
				</SettingsConsumer>

			</StyledEngineProvider>
	);
});

export default App;
